import { clientEnv } from "@/env/client";

const DATADOG_RUM_SDK_URL =
  "https://www.datadoghq-browser-agent.com/ap1/v5/datadog-rum.js";

/** beforeSend が触る RUM イベントのプロパティ */
type RumEvent = {
  type: string;
  view?: { url: string; referrer?: string };
};

const parseUrl = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

/**
 * 計測しない画面の URL を、画面の種類までのパス（excludedPathPattern の match の [0]）に切り詰める。
 * トークンや ID を伏せるため。計測しない画面でなければそのまま返す
 */
const redactExcludedUrl = (url: string, excludedPathPattern: RegExp) => {
  const parsed = parseUrl(url);
  const matched = parsed?.pathname.match(excludedPathPattern);
  if (parsed == null || matched == null) {
    return url;
  }
  return `${parsed.origin}${matched[0]}`;
};

/**
 * 計測しない画面のイベントを RUM に送らないための beforeSend。
 * 画面内の移動で計測しない画面へ入った場合に、その画面の操作・リソース・エラーを捨てる。
 * 閲覧（view）は RUM の仕様上捨てられないため、URL と参照元を切り詰めてトークン等を伏せる
 */
const createBeforeSend =
  (excludedPathPattern: RegExp) =>
  (event: RumEvent): boolean => {
    if (event.view == null) {
      return true;
    }
    const pathname = parseUrl(event.view.url)?.pathname;
    if (pathname != null && excludedPathPattern.test(pathname)) {
      if (event.type !== "view") {
        return false;
      }
      event.view.url = redactExcludedUrl(event.view.url, excludedPathPattern);
    }
    if (event.view.referrer != null) {
      event.view.referrer = redactExcludedUrl(
        event.view.referrer,
        excludedPathPattern,
      );
    }
    return true;
  };

/**
 * テスト専用のexport。
 * createBeforeSend はこのファイルの loadDatadogRum からしか使わないが、イベントの捨て方・URL の切り詰め方を
 * 単体で確かめるために公開する。
 *
 * @knipignore
 */
export const forTest = {
  createBeforeSend,
};

let loaded = false;

/**
 * Datadog RUM の SDK を読み込んで初期化する。セッション中に一度だけ行う。
 * Datadog 公式の CDN 非同期読み込みと同じく、SDK の読み込み前は onReady をキューに積むスタブを置く
 */
export const loadDatadogRum = (excludedPathPattern: RegExp) => {
  if (loaded) {
    return;
  }
  if (
    !clientEnv.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ||
    !clientEnv.NEXT_PUBLIC_DATADOG_APPLICATION_ID
  ) {
    return;
  }
  loaded = true;

  const stub: NonNullable<Window["DD_RUM"]> = {
    q: [],
    onReady: (callback) => {
      stub.q?.push(callback);
    },
  };
  window.DD_RUM = window.DD_RUM ?? stub;

  const script = document.createElement("script");
  script.async = true;
  script.src = DATADOG_RUM_SDK_URL;
  document.head.appendChild(script);

  window.DD_RUM.onReady(() => {
    window.DD_RUM?.init?.({
      clientToken: clientEnv.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
      applicationId: clientEnv.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
      site: "ap1.datadoghq.com",
      service: "mykenpo",
      env: clientEnv.NEXT_PUBLIC_VERCEL_ENV,
      version: clientEnv.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || "unknown",
      sessionSampleRate: clientEnv.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      beforeSend: createBeforeSend(excludedPathPattern),
    });
  });
};
